import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Drawer,
  Avatar,
  Chip,
  Stack,
  Divider,
  Tooltip,
  Rating,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  WhatsApp,
  Email,
  Notifications,
  ContentCopy,
  CheckCircle,
  Cancel,
  Check,
  AccountCircle,
  Phone,
  HandymanRounded,
  Send,
  TextSnippet,
  Warning,
  Task,
  UploadFile,
  Upload,
  AttachFile,
  TextSnippetOutlined,
} from "@mui/icons-material";
import SmsIcon from "@mui/icons-material/Sms";
import getDocumentTitle from "../services/common";
import stringToColor from "../services/stringToColor";
import axios from "axios";
import { useUser } from "../provider/AuthProvider";
import PictureEditor from "./PictureEditor";

const ServiceProviderDrawer = ({ open, onClose, provider, onUpdate }) => {
  const [copied, setCopied] = useState(false);
  const [availableServiceTypes, setAvailableServiceTypes] = useState([]);

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [loading, setLoading] = useState(false);
  const { user } = useUser();
  const [isSaving, setIsSaving] = useState(false);
  const [editedFields, setEditedFields] = useState({
    username: "",
    email: "",
    phone: "",
    document: "",
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [avatarUploading, setAvatarUploading] = useState(false);

  useEffect(() => {
    if (provider) {
      setEditedFields({
        username: provider.username || "",
        email: provider.email || "",
        phone: provider.phone || "",
        document: provider.document || "",
        serviceType: provider.compoundserviceType || "",
      });
      setSelectedFile(null); // Reset selected file when provider changes
    }
    const fetchServiceTypes = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/cmt/availableServiceTypeList/${user.id}`
        );
        setAvailableServiceTypes(response.data);
        console.log(response.data);
      } catch (error) {
        console.error("Error fetching service types:", error);
      }
    };

    fetchServiceTypes();
  }, [provider]);

  const handleSaveUser = async () => {
    setIsSaving(true);
    try {
      const updatePayload = {};
      const userFieldsChanged =
        provider.username !== editedFields.username ||
        provider.email !== editedFields.email ||
        provider.phone !== editedFields.phone;

      if (userFieldsChanged) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/compound/updateUser/${provider.id}`,
          {
            username: editedFields.username,
            email: editedFields.email,
            phone: editedFields.phone,
          }
        );
        if (editedFields.username)
          updatePayload.username = editedFields.username;
        if (editedFields.email) updatePayload.email = editedFields.email;
        if (editedFields.phone) updatePayload.phone = editedFields.phone;
      }
      if (provider.compoundServicetype !== editedFields.serviceType) {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/cmt/assignService`,
          {
            provider_id: provider.id,
            serviceType: editedFields.serviceType,
          }
        );
        updatePayload.compoundserviceType = editedFields.serviceType;
      }
      const updatedProvider = { ...provider, ...updatePayload };
      if (onUpdate) onUpdate(updatedProvider);
      setSnack({
        open: true,
        message: "User information updated successfully.",
        severity: "success",
      });
    } catch (err) {
      setSnack({
        open: true,
        message: "Failed to update user info.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };
  if (!provider) {
    return (
      <Drawer anchor="right" open={open} onClose={onClose}>
        <Box
          sx={{
            width: 500,
            p: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      </Drawer>
    );
  }

  const hasChanges = () => {
    return (
      provider &&
      (provider.username !== editedFields.username ||
        provider.email !== editedFields.email ||
        provider.phone !== editedFields.phone ||
        provider.compoundserviceType !== editedFields.serviceType)
    );
  };

  const communication_method =
    provider.CommunicationMethod === "both"
      ? "WhatsApp, Email"
      : provider.CommunicationMethod;

  const handleCopy = async () => {
    setCopied(true);
    try {
      await navigator.clipboard.writeText(provider.document);
      setSnack({
        open: true,
        message: `Copied: ${provider.document}`,
        severity: "success",
      });
    } catch {
      setSnack({
        open: true,
        message: "Failed to copy link.",
        severity: "error",
      });
    } finally {
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleStatusChange = async (status) => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/cmt/userActive`,
        {
          id: provider.id,
          user_id: user.id,
          status,
        }
      );
      const updatedProvider = { ...provider, status };
      if (onUpdate) onUpdate(updatedProvider);
      setSnack({
        open: true,
        message: `Provider ${status}d successfully.`,
        severity: "success",
      });
    } catch {
      setSnack({
        open: true,
        message: `Failed to change provider status.`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 500, p: 3 }}>
        <Box display="flex" justifyContent="center">
          <PictureEditor
            initialImage={provider.avatar_url}
            onImageChange={async (newUrl) => {
              // update the backend
              await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/compound/updateUser/${provider.id}`,
                { avatar_url: newUrl }
              );
              // update the parent state/UI
              if (onUpdate) onUpdate({ ...provider, avatar_url: newUrl });
            }}
            size={80}
          />

          {/* <Avatar src={provider.avatar_url} sx={{ width: 80, height: 80 }} /> */}
        </Box>
        <Typography align="center" variant="h6" mt={1} className="notranslate">
          {provider.fullname}
        </Typography>

        <Box display="flex" justifyContent="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Rating
              value={provider.rating ?? 0}
              precision={0.5}
              readOnly
              size="large"
              sx={{ mt: 0.5 }}
            />
            <Typography variant="body2">{provider.rating}</Typography>
            <Task />
            <Typography variant="body2">{provider.complete_task}</Typography>
          </Stack>
        </Box>

        <Box display="flex" justifyContent="center" gap={2}>
          <Chip
            icon={provider.verify ? <Check /> : <Cancel />}
            label={provider.verify ? "Verified" : "Non-Verified"}
            color={provider.verify ? "success" : "default"}
            size="small"
          />
          <Chip
            icon={provider.status === "active" ? <CheckCircle /> : <Cancel />}
            label={provider.status.toUpperCase()}
            color={
              provider.status === "active"
                ? "success"
                : provider.status === "pending"
                ? "primary"
                : "error"
            }
            size="small"
          />
        </Box>

        <Box mt={2} display="flex" justifyContent="center" gap={2}>
          <Tooltip title="WhatsApp" className="notranslate">
            <IconButton
              color="success"
              onClick={() => {
                const Phone = provider.phone?.replace(/\D/g, ""); // sanitize
                const message = encodeURIComponent(
                  "Hello! I'm contacting you via Justenant."
                );
                window.open(`https://wa.me/${Phone}?text=${message}`, "_blank");
              }}
            >
              <WhatsApp />
            </IconButton>
          </Tooltip>
          <Tooltip title="Email" className="notranslate">
            <a
              href={`mailto:${provider.email}?subject=Hello&body=Just writing to say hi.`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit" }}
            >
              <IconButton color="info">
                <Email />
              </IconButton>
            </a>
          </Tooltip>
        </Box>

        <Box mt={2}>
          <Divider />
          <TextField
            label="Username"
            value={editedFields.username}
            onChange={(e) =>
              setEditedFields((prev) => ({ ...prev, username: e.target.value }))
            }
            margin="normal"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccountCircle />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Email"
            value={editedFields.email}
            onChange={(e) =>
              setEditedFields((prev) => ({ ...prev, email: e.target.value }))
            }
            margin="normal"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Phone"
            value={editedFields.phone}
            onChange={(e) =>
              setEditedFields((prev) => ({ ...prev, phone: e.target.value }))
            }
            margin="normal"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Phone />
                </InputAdornment>
              ),
            }}
            dir="ltr"
          />
          {/* <TextField
            label="Communication Method"
            defaultValue={communication_method}
            margin="normal"
            fullWidth
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SmsIcon />
                </InputAdornment>
              ),
            }}
          /> */}

          <FormControl fullWidth margin="normal">
            <InputLabel id="service-type-label">Service Type</InputLabel>
            <Select
              labelId="service-type-label"
              value={editedFields.serviceType}
              label="Service Type"
              onChange={(e) =>
                setEditedFields((prev) => ({
                  ...prev,
                  serviceType: e.target.value,
                }))
              }
              startAdornment={
                <InputAdornment position="start">
                  <HandymanRounded />
                </InputAdornment>
              }
            >
              {availableServiceTypes.map((type) => {
                const isAssigned = type.name === provider.compoundserviceType;
                const isRegistered =
                  type.name === provider.registrationsServiceType;

                return (
                  <MenuItem key={type.name} value={type.name}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: isAssigned ? "green" : "inherit",
                          fontWeight: isRegistered ? 600 : 400,
                        }}
                      >
                        {type.name}
                      </Typography>
                      <Box>
                        {isAssigned && (
                          <Chip
                            label="assigned"
                            size="small"
                            color="success"
                            sx={{ ml: 1 }}
                          />
                        )}
                        {isRegistered && (
                          <Chip
                            label="request"
                            size="small"
                            color="warning"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <TextField
            label="Document"
            value={getDocumentTitle(editedFields.document) || ""}
            margin="normal"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <TextSnippet />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={"View Document"}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const fileUrl = encodeURIComponent(provider.document);
                        const viewerUrl = `https://docs.google.com/gview?url=${fileUrl}&embedded=true`;
                        window.open(viewerUrl, "_blank");
                      }}
                    >
                      <TextSnippet fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={"Select Document"}>
                    <label htmlFor="attach-file">
                      <IconButton component="span" size="small">
                        <AttachFile fontSize="small" />
                      </IconButton>
                    </label>
                  </Tooltip>
                  <Tooltip title={copied ? "Copied" : "Copy Link"}>
                    <IconButton size="small" onClick={handleCopy}>
                      {copied ? (
                        <CheckCircle fontSize="small" color="success" />
                      ) : (
                        <ContentCopy fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            id="attach-file"
            style={{ display: "none" }}
            onChange={(e) => {
              if (e.target.files.length > 0) {
                setSelectedFile(e.target.files[0]);
              }
            }}
          />
          {selectedFile && (
            <Grid container alignItems="center" spacing={2} mt={1}>
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  <strong>{selectedFile.name}</strong>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box mt={1} display="flex" gap={1}>
                  <Tooltip title="Upload">
                    <span>
                      <IconButton
                        color="primary"
                        onClick={async () => {
                          setUploading(true);
                          try {
                            const formData = new FormData();
                            formData.append("file", selectedFile);
                            formData.append("user_id", provider.id);

                            // Upload file
                            const res = await axios.post(
                              `${process.env.REACT_APP_BACKEND_URL}/api/upload/user/document`,
                              formData
                            );
                            console.log(res.data);
                            const fileUrl = res.data; // Assume backend returns { url: "https://..." }
                            const fileName = fileUrl.split("/").pop();
                            // Call updateUser to save URL in DB
                            await axios.put(
                              `${process.env.REACT_APP_BACKEND_URL}/api/compound/updateUser/${provider.id}`,
                              { document: fileUrl }
                            );

                            // Update provider and show success
                            if (onUpdate) {
                              onUpdate({ ...provider, document: fileUrl });
                            }
                            setEditedFields((prev) => ({
                              ...prev,
                              document: fileName,
                            }));
                            setSnack({
                              open: true,
                              message:
                                "Document uploaded and saved successfully.",
                              severity: "success",
                            });
                            setSelectedFile(null);
                          } catch (error) {
                            setSnack({
                              open: true,
                              message: "Failed to upload or update document.",
                              severity: "error",
                            });
                          } finally {
                            setUploading(false);
                          }
                        }}
                        disabled={uploading}
                      >
                        {uploading ? (
                          <CircularProgress size={24} />
                        ) : (
                          <Upload />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Tooltip title="Cancel">
                    <IconButton
                      color="error"
                      onClick={() => setSelectedFile(null)}
                      disabled={uploading}
                    >
                      <Cancel />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          )}
        </Box>

        <Box mt={2}>
          <Divider />
          {!provider.verify && (
            <Box display="flex" alignItems="center" mt={2}>
              <Warning color="warning" sx={{ mr: 1 }} />
              <Typography variant="body2" flexGrow={1} color="warning">
                This user is not verified.
              </Typography>
              {/* <IconButton>
                <Send />
              </IconButton> */}
            </Box>
          )}

          <Box display="flex" justifyContent="center" mt={2} gap={2}>
            {provider.status === "pending" && (
              <>
                <Button
                  color="success"
                  variant="contained"
                  onClick={() => handleStatusChange("active")}
                  sx={{ color: "#fff" }}
                  disabled={loading}
                >
                  {loading ? "changing..." : "ACTIVE"}
                </Button>
                <Button
                  color="error"
                  variant="contained"
                  onClick={() => handleStatusChange("inactive")}
                  sx={{ color: "#fff" }}
                  disabled={loading}
                >
                  {loading ? "changing..." : "INACTIVE"}
                </Button>
              </>
            )}
            {provider.status === "active" && (
              <Button
                color="error"
                variant="contained"
                onClick={() => handleStatusChange("inactive")}
                sx={{ color: "#fff" }}
                disabled={loading}
              >
                {loading ? "changing..." : "INACTIVE"}
              </Button>
            )}
            {provider.status === "inactive" && (
              <Button
                color="success"
                variant="contained"
                onClick={() => handleStatusChange("active")}
                sx={{ color: "#fff" }}
                disabled={loading}
              >
                {loading ? "changing..." : "ACTIVE"}
              </Button>
            )}
            <Button
              color="info"
              variant="contained"
              onClick={handleSaveUser}
              disabled={!hasChanges() || isSaving}
              sx={{ color: "#fff" }}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </Box>
        </Box>

        <Snackbar
          open={snack.open}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          onClose={() => setSnack({ ...snack, open: false })}
        >
          <Alert
            severity={snack.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snack.message}
          </Alert>
        </Snackbar>
      </Box>
    </Drawer>
  );
};

export default ServiceProviderDrawer;
